/**
 * Legal entity detail — the entity, the companies it owns, and its
 * activate / deactivate lifecycle.
 *
 * Deactivating is the consequential action here: an inactive entity cannot be
 * posted to, so the confirmation says what it will stop rather than asking a
 * bare "are you sure?".
 *
 * Arabic and English (C7): every word from the catalogs; the tabs are chosen
 * by id, never by their words; country and currency are named in the
 * interface's language.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, useApiList, useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { formatDate } from "@/lib/money";
import { countryName, currencyName } from "@/lib/reference";
import type { CompanyRow, LegalEntityRow } from "./types";

export function LegalEntityDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const [tab, setTab] = useState<string>("overview");

  const entity = useApiQuery<LegalEntityRow>(
    ["org", "legal-entity", id],
    `/legal-entities/${id}`,
    {
      enabled: Boolean(id),
    },
  );
  const companies = useApiList<CompanyRow>(["org", "companies"], "/companies");

  const activate = useApiMutation<void, unknown>(
    ["org", "legal-entity", id],
    () => post(`/legal-entities/${id}/actions/activate`, {}),
  );
  const inactivate = useApiMutation<void, unknown>(
    ["org", "legal-entity", id],
    () => post(`/legal-entities/${id}/actions/inactivate`, {}),
  );

  if (entity.isLoading) return <Skeleton rows={10} />;
  if (entity.error || !entity.data) {
    return (
      <EmptyState
        title={t("org.legalEntity.notFound")}
        message={entity.error ? undefined : t("org.legalEntity.archived")}
        action={
          entity.error ? (
            <div role="alert" style={{ fontSize: 14 }}>
              <ErrorText error={entity.error} />
            </div>
          ) : undefined
        }
      />
    );
  }

  const row = entity.data;
  const owned = (companies.data?.items ?? []).filter(
    (c) => c.legal_entity_id === row.id,
  );
  const busy = activate.isPending || inactivate.isPending;
  const failure = activate.error ?? inactivate.error;

  return (
    <div>
      <PageHeader
        title={row.name}
        subtitle={
          row.legal_name !== row.name
            ? t("org.legalEntity.registeredAs", { name: row.legal_name })
            : undefined
        }
        breadcrumb={[t("org.section"), t("org.legalEntities.title"), row.name]}
        actions={
          row.status === "Active" ? (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm(
                    t("org.legalEntity.confirmDeactivate", { name: row.name }),
                  )
                ) {
                  inactivate.mutate();
                }
              }}
            >
              {inactivate.isPending
                ? t("org.legalEntity.deactivating")
                : t("org.legalEntity.deactivate")}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => activate.mutate()}
            >
              {activate.isPending
                ? t("org.legalEntity.activating")
                : t("org.legalEntity.activate")}
            </Button>
          )
        }
      />

      {failure && (
        <Card className="mb-4">
          <div role="alert" style={{ color: "var(--atlas-danger)" }}>
            <ErrorText error={failure} />
          </div>
        </Card>
      )}

      <Tabs
        tabs={[
          { id: "overview", label: t("org.legalEntity.overview") },
          {
            id: "companies",
            label: t("org.legalEntity.companiesTab", { count: owned.length }),
          },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "overview" && (
        <Card title={t("org.legalEntity.registration")} className="mt-4">
          <dl className="atlas-grid atlas-grid--3">
            <div>
              <dt className="atlas-type-label">{t("fields.status")}</dt>
              <dd>
                <StatusBadge status={row.status} type="LegalEntity" />
              </dd>
            </div>
            <div>
              <dt className="atlas-type-label">
                {t("org.legalEntities.country")}
              </dt>
              <dd>{countryName(row.registration_country, i18n.language)}</dd>
            </div>
            <div>
              <dt className="atlas-type-label">
                {t("org.legalEntities.currency")}
              </dt>
              <dd>
                <span className="atlas-mono">{row.currency_code}</span>{" "}
                <span className="atlas-muted">
                  {currencyName(row.currency_code, i18n.language)}
                </span>
              </dd>
            </div>
            <div>
              <dt className="atlas-type-label">
                {t("org.legalEntity.taxRegistration")}
              </dt>
              <dd className="atlas-mono">{row.tax_id ?? "—"}</dd>
            </div>
            <div>
              <dt className="atlas-type-label">{t("fields.created")}</dt>
              <dd>{formatDate(row.created_at)}</dd>
            </div>
          </dl>
        </Card>
      )}

      {tab === "companies" && (
        <Card title={t("org.legalEntity.companiesTitle")} className="mt-4">
          {owned.length === 0 ? (
            <EmptyState
              title={t("org.legalEntity.noCompanies")}
              message={t("org.legalEntity.noCompaniesMessage")}
              action={
                <Link to="/app/organization/companies">
                  <Button variant="primary">
                    {t("org.legalEntity.addCompany")}
                  </Button>
                </Link>
              }
            />
          ) : (
            <DataTable<CompanyRow>
              rows={owned}
              columns={[
                {
                  key: "name",
                  label: t("fields.name"),
                  render: (c) => (
                    <Link to={`/app/organization/companies/${c.id}`}>
                      {c.name}
                    </Link>
                  ),
                },
                {
                  key: "code",
                  label: t("fields.code"),
                  render: (c) => <span className="atlas-mono">{c.code}</span>,
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (c) => (
                    <StatusBadge status={c.status} type="Company" />
                  ),
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
